import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * ACTIVE user blocks for the signed-in viewer (`public.user_blocks` where
 * `unblocked_at IS NULL`). Used to filter blocked authors out of every LR Hub
 * read path and to enforce the messaging block in the UI (the DB trigger
 * `enforce_message_block_trg` enforces it server-side).
 *
 * Unblocking happens on /safety — clearing `unblocked_at` there makes the rows
 * disappear from this list, so Hub content and DMs resume normally.
 */

export const HUB_BLOCKED_IDS_KEY = ["hub-blocked-user-ids"] as const;

export function useBlockedUserIds() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: [...HUB_BLOCKED_IDS_KEY, userId ?? "anon"],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("user_blocks")
        .select("blocked_user_id")
        .eq("blocker_id", userId!)
        .is("unblocked_at", null)
        .not("blocked_user_id", "is", null);
      if (error) throw error;
      return Array.from(
        new Set(((data ?? []) as { blocked_user_id: string | null }[])
          .map((r) => r.blocked_user_id)
          .filter((v): v is string => !!v)),
      ).sort();
    },
  });

  return { blockedIds: query.data ?? [], isLoading: query.isLoading };
}

/** Blocks a Hub author by USER ID (never through resolve_block_target). */
export function useBlockHubUser() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useCallback(
    async (blockedUserId: string, readableLabel: string) => {
      if (!user?.id) throw new Error("You must be signed in.");
      const { error } = await supabase.from("user_blocks").insert({
        blocker_id: user.id,
        blocked_user_id: blockedUserId,
        blocked_identifier: readableLabel || blockedUserId,
      });
      // 23505/23505-style unique violation = already actively blocked.
      if (error && error.code !== "23505") throw error;

      await qc.invalidateQueries({ queryKey: HUB_BLOCKED_IDS_KEY });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["hub-feed"] }),
        qc.invalidateQueries({ queryKey: ["hub-comments"] }),
        qc.invalidateQueries({ queryKey: ["hub-recent-posts"] }),
        qc.invalidateQueries({ queryKey: ["hub-recent-comments"] }),
        qc.invalidateQueries({ queryKey: ["hub-recently-active"] }),
        qc.invalidateQueries({ queryKey: ["hub-pinned-post"] }),
      ]);
    },
    [qc, user?.id],
  );
}

export default useBlockedUserIds;

/**
 * Is there an ACTIVE block in EITHER direction between the viewer and `otherId`?
 * Mirrors the `enforce_message_block_trg` DB trigger so the composer can be
 * disabled — the trigger, not this hook, is what actually enforces it.
 */
export function useIsBlockedPair(otherId: string | null | undefined) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ["users-block-active", user?.id ?? "anon", otherId ?? "none"],
    enabled: !!user?.id && !!otherId,
    staleTime: 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await (supabase.rpc as any)("users_block_active", {
        _a: user!.id,
        _b: otherId,
      });
      if (error) throw error;
      return !!data;
    },
  });
  return query.data === true;
}
